"use client";
import { TLocale } from "@/lib/@types/locale";
import { SITE_LOCALES } from "@/lib/constant";
import React, { useEffect, useState, useRef, useCallback, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { usePathname } from "@/navigation";
import { useLocale } from "next-intl";
import { defaultLocale } from "@/config";
import { Link } from "@/navigation";
import FlagIcon from "@/components/Flag";
import clsx from "clsx";

// 内部组件：使用 useSearchParams
const TranslateContent = ({ 
	currentLocales, 
	isOpenLanguage, 
	currentSlug, 
	locale, 
	handleMouseEnter, 
	handleMouseLeave,
	isVisible
}: {
	currentLocales: TLocale[];
	isOpenLanguage: boolean;
	currentSlug: string;
	locale: string;
	handleMouseEnter: () => void;
	handleMouseLeave: () => void;
	isVisible?: boolean;
}) => {
	const searchParams = useSearchParams();
	const activeLocale = currentLocales.find((item) => item.code === locale);
	const activeLocaleShortCode = (activeLocale?.code ?? locale).split("-")[0].toUpperCase();

	return (
		<div
			className="relative"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<button
				type="button"
				className={clsx(
					"flex h-9 items-center gap-1 rounded-full px-2.5 text-[13px] font-medium tracking-wide transition-colors duration-200",
					isVisible
						? "text-gray-700 hover:bg-mainColor/10 hover:text-mainColor"
						: "text-white hover:bg-white/15"
				)}
				aria-expanded={isOpenLanguage}
				aria-haspopup="true"
				title={activeLocale?.nativeName}
			>
				<i className="ri-global-line text-[18px] leading-none" aria-hidden="true" />
				<span>{activeLocaleShortCode}</span>
				<i
					className={clsx(
						"ri-arrow-down-s-line text-base leading-none transition-transform duration-200",
						isOpenLanguage && "rotate-180"
					)}
					aria-hidden="true"
				/>
			</button>

			{isOpenLanguage && (
				<div className="absolute right-0 top-full z-50 pt-2">
					<div
						className="min-w-[168px] overflow-hidden rounded-lg border border-gray-100 bg-white py-1.5 shadow-xl"
						role="menu"
						aria-orientation="vertical"
					>
						{currentLocales.map((item) => {
							const queryString = searchParams?.toString();
							const hrefWithQuery = queryString ? `${currentSlug}?${queryString}` : currentSlug;
							const isActiveLocale = locale === item.code;

							return (
								<Link
									key={item.code}
									href={hrefWithQuery}
									locale={item.code}
									className={clsx(
										"flex items-center gap-2.5 whitespace-nowrap px-4 py-2 text-sm transition-colors",
										isActiveLocale
											? "bg-mainColor/10 font-medium text-mainColor"
											: "text-gray-700 hover:bg-gray-50 hover:text-mainColor"
									)}
									role="menuitem"
									onClick={() => {
										if (typeof window !== 'undefined') {
											window.dispatchEvent(new Event('languageChanged'));
										}
									}}
								>
									<FlagIcon flag={item.flag} />
									<span className="flex-1">{item.nativeName}</span>
									{isActiveLocale && <i className="ri-check-line text-base" aria-hidden="true" />}
								</Link>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

const Translate = ({ isVisible }: { isVisible?: boolean }) => {
	const currentLocales: TLocale[] = SITE_LOCALES;
	const [isOpenLanguage, setIsOpenLanguage] = useState(false);
	const pathname = usePathname();
	const locale = useLocale();
	const [currentSlug, setCurrentSlug] = useState("");
	const dropdownRef = useRef<HTMLDivElement>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const getCurrentLocale = useCallback(() => {
		return currentLocales.find((item) => item.code === locale);
	}, [currentLocales, locale]);

	// 修改鼠标进入事件，立即显示下拉菜单
	const handleMouseEnter = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setIsOpenLanguage(true);
	}, []);

	// 修改鼠标离开事件，添加延迟关闭
	const handleMouseLeave = useCallback(() => {
		timeoutRef.current = setTimeout(() => {
			setIsOpenLanguage(false);
			timeoutRef.current = null;
		}, 200); // 200ms的延迟，给用户足够时间移动鼠标
	}, []);

	useEffect(() => {
		if (pathname) {
			// usePathname from @/navigation already returns the unlocalized path
			setCurrentSlug(pathname);
		}
	}, [pathname]);

	// 清理timeout，避免内存泄漏
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	if (process.env.NEXT_PUBLIC_IS_I18N !== "true") {
		return null;
	}

	return (
		<Suspense fallback={
			<button
				type="button"
				className={clsx(
					"flex h-9 items-center gap-1 rounded-full px-2.5 text-[13px] font-medium tracking-wide",
					isVisible ? "text-gray-700" : "text-white"
				)}
			>
				<i className="ri-global-line text-[18px] leading-none" aria-hidden="true" />
				<span>{(getCurrentLocale()?.code ?? locale).split("-")[0].toUpperCase()}</span>
				<i className="ri-arrow-down-s-line text-base leading-none" aria-hidden="true" />
			</button>
		}>
			<TranslateContent
				currentLocales={currentLocales}
				isOpenLanguage={isOpenLanguage}
				currentSlug={currentSlug}
				locale={locale}
				handleMouseEnter={handleMouseEnter}
				handleMouseLeave={handleMouseLeave}
				isVisible={isVisible}
			/>
		</Suspense>
	);
};

export default Translate;
